import { useCallback, useEffect, useState } from "react";
import { useSearch } from "wouter";
import type { RingInvoice, RingLimits } from "@clevis/shared-types";
import { Button, Card, CardTitle, cn } from "@clevis/shared-ui";
import { call } from "../lib/callFunction";

// Clevis Ring's money side, as the company sees it. Ring runs on real phone
// numbers from the carrier, and the carrier's charges pass through to the
// company at Clevis's posted rates, on top of (and apart from) the plan. So a
// company turns Ring on by agreeing to that and keeping a card on file, the
// way a hotel takes a card for incidentals on a comped room. Stripe's hosted
// page takes the card; Clevis sees the brand and last four.

export interface RingStatus {
  rates: { numberMonthlyCents: number; minuteCents: number };
  agreed: { at: number; byEmail: string } | null;
  card: { brand: string; last4: string; viaSubscription: boolean } | null;
  limits: RingLimits;
  numbersActive: number;
  numberCap: number;
  usage: { month: string; minutes: number; calls: number };
  estimateCents: number;
  invoices: RingInvoice[];
}

const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const cents = (c: number) => (c % 100 === 0 ? money(c) : c < 100 ? `${c}¢` : money(c));
const monthName = (key: string) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(Date.UTC(y ?? 2026, (m ?? 1) - 1, 1)).toLocaleString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
};
const when = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
const cardLabel = (card: NonNullable<RingStatus["card"]>) => (card.viaSubscription ? "the card on your Clevis subscription" : `${card.brand ? card.brand[0]!.toUpperCase() + card.brand.slice(1) : "Card"} ···${card.last4}`);

export function useRingStatus(tenantId: string) {
  const [status, setStatus] = useState<RingStatus | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      setStatus(await call<RingStatus>("getRingStatus", { tenantId }, { mutation: false }));
      setError("");
    } catch (e) {
      setError((e as Error).message);
    }
  }, [tenantId]);
  useEffect(() => {
    void load();
  }, [load]);
  return { status, error, reload: load };
}

async function goAddCard(tenantId: string) {
  const r = await call<{ url: string }>("startRingCardSetup", { tenantId }, { mutation: false });
  window.location.href = r.url;
}

/** Setup → Clevis Ring: the agreement, the card, this month's usage and past charges. */
export function RingSetup({ tenantId, onReady }: { tenantId: string; onReady?: (ready: boolean) => void }) {
  const { status: s, error, reload } = useRingStatus(tenantId);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const search = useSearch();

  // Back from Stripe's card page: the webhook lands a moment later.
  useEffect(() => {
    const card = new URLSearchParams(search).get("card");
    if (!card) return;
    setNote(card === "added" ? "Card saved." : "No card was added.");
    window.history.replaceState(null, "", "/leads");
    const t = setTimeout(() => void reload(), 2000);
    return () => clearTimeout(t);
  }, [search, reload]);

  const ready = !!s?.agreed && !!s?.card;
  useEffect(() => {
    onReady?.(ready);
  }, [ready, onReady]);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setNote("");
    try {
      await fn();
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!s) return <p className="text-xs text-steel">{error || "Loading Clevis Ring…"}</p>;
  const r = s.rates;
  const example = 2 * r.numberMonthlyCents + 300 * r.minuteCents;

  if (!s.agreed || !s.card) {
    return (
      <Card className="space-y-2 border-brandorange/50">
        <CardTitle>{s.agreed ? "Add a card for the carrier charges" : "Turn on Clevis Ring"}</CardTitle>
        {note ? <p className="rounded-lg bg-cream px-3 py-1.5 text-xs text-ink">{note}</p> : null}
        <p className="text-sm text-ink/90">
          Clevis Ring runs on real phone numbers from the phone carrier. The carrier charges for them, and Clevis passes those charges through to you at cost, separate from your Clevis plan:{" "}
          <span className="font-semibold">{money(r.numberMonthlyCents)} per number a month</span> and <span className="font-semibold">{cents(r.minuteCents)} per forwarded minute</span>, counted from the moment
          your line answers until the call ends.
        </p>
        <p className="text-sm text-ink/90">
          This is a carrier charge, not a Clevis fee, and it applies whether or not your Clevis plan is paid. On the 1st of each month, last month's numbers and minutes are charged to the card you keep on file, and
          you get the breakdown by email. Example: 2 numbers and 300 minutes come to {money(example)}.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {!s.agreed ? (
            <Button
              type="button"
              busy={busy}
              onClick={() =>
                void act(async () => {
                  const a = await call<{ needsCard: boolean }>("agreeToRing", { tenantId });
                  if (a.needsCard) await goAddCard(tenantId);
                  else await reload();
                })
              }
            >
              Agree and continue
            </Button>
          ) : (
            <Button type="button" busy={busy} onClick={() => void act(() => goAddCard(tenantId))}>
              Add a card
            </Button>
          )}
          <span className="text-xs text-steel">{s.agreed ? "Stripe takes the card; Clevis keeps the brand and last four." : "Next: a card on Stripe's page, unless your Clevis subscription already has one."}</span>
        </div>
      </Card>
    );
  }

  const minCap = s.limits.minutesPerMonth;
  const pct = minCap ? Math.min(100, Math.round((s.usage.minutes / minCap) * 100)) : null;
  return (
    <Card className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle className="mb-0">Clevis Ring is on</CardTitle>
        <Button type="button" variant="ghost" className="px-2 py-0.5 text-xs" busy={busy} onClick={() => void act(() => goAddCard(tenantId))}>
          {s.card.viaSubscription ? "Use a different card" : "Change card"}
        </Button>
      </div>
      {note ? <p className="rounded-lg bg-cream px-3 py-1.5 text-xs text-ink">{note}</p> : null}
      <p className="text-sm text-ink">
        Carrier charges go to {cardLabel(s.card)}: {money(r.numberMonthlyCents)} per number a month, {cents(r.minuteCents)} per forwarded minute, billed on the 1st.
      </p>
      <p className="text-sm text-ink">
        {monthName(s.usage.month)} so far: <span className="font-semibold">{s.usage.minutes.toLocaleString()} minutes</span>
        {minCap ? ` of ${minCap.toLocaleString()}` : ""} over {s.usage.calls} call{s.usage.calls === 1 ? "" : "s"}, {s.numbersActive} number{s.numbersActive === 1 ? "" : "s"}
        {s.numberCap ? ` of ${s.numberCap}` : ""}: about {money(s.estimateCents)}.
        {pct !== null && pct >= 80 ? (
          <span className={cn("ml-1 font-semibold", pct >= 100 ? "text-danger" : "text-brandorange-dark")}>
            {pct >= 100 ? (s.limits.hardStop ? "At the cap: callers hear the line can't take calls until next month." : "Past the cap: calls still go through and the extra minutes bill at the same rate.") : `${pct}% of the cap.`}
          </span>
        ) : null}
      </p>
      {s.invoices.length ? (
        <div className="text-xs">
          <div className="mb-1 font-semibold text-ink">Past months</div>
          {s.invoices.map((inv) => (
            <div key={inv.month} className="flex flex-wrap items-center justify-between gap-2 border-t border-steel/10 py-1">
              <span className="text-ink">
                {monthName(inv.month)} · {inv.numbers} number{inv.numbers === 1 ? "" : "s"}, {inv.minutes.toLocaleString()} min
              </span>
              <span className={cn(inv.status === "paid" || inv.status === "nothing" ? "text-steel" : "font-semibold text-danger")}>
                {money(inv.amountCents)} · {inv.status === "paid" ? "charged" : inv.status === "nothing" ? "nothing to charge" : inv.status === "no_card" ? "no card on file" : `didn't go through: ${inv.error}`}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      <p className="text-[11px] text-steel">
        Agreed by {s.agreed.byEmail || "an admin"} on {when(s.agreed.at)}. These are the carrier's charges passed through, not a Clevis fee.
      </p>
    </Card>
  );
}

/** Setup → Billing: the incidentals card in one line. */
export function IncidentalsCard({ tenantId }: { tenantId: string }) {
  const { status: s } = useRingStatus(tenantId);
  const [busy, setBusy] = useState(false);
  if (!s) return null;
  return (
    <Card>
      <CardTitle>Card for incidentals</CardTitle>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-ink">
        <span>
          {s.card ? `${cardLabel(s.card)} covers pass-through charges like Clevis Ring's phone numbers and minutes.` : "None yet. Clevis Ring's phone numbers and minutes are carrier charges passed through to a card, even on a comped plan."}
        </span>
        <Button
          type="button"
          variant="ghost"
          className="px-2 py-1 text-xs"
          busy={busy}
          onClick={() => {
            setBusy(true);
            void goAddCard(tenantId).catch(() => setBusy(false));
          }}
        >
          {s.card && !s.card.viaSubscription ? "Change card" : "Add a card"}
        </Button>
      </div>
    </Card>
  );
}
